import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Spinner } from "../Bits";
import ImageUpload from "../ImageUpload";
import { Plus, Trash2, Save, Eye, EyeOff, Megaphone, MessageSquare } from "lucide-react";

const DEFAULT_BANNER = {
  kind: "banner",
  title: "",
  message: "Neuer Hinweis für deine Besucher",
  cta_text: "",
  cta_link: "",
  bg_color: "#5a6e3f",
  text_color: "#FCF7EC",
  image_url: "",
  position: "top",
  delay_seconds: 7,
  active: false,
};

const DEFAULT_POPUP = { ...DEFAULT_BANNER, kind: "popup", title: "Schön, dass du da bist!", message: "Eine kurze Info für dich …" };

export default function MessagesAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/site-messages/admin").then((r) => setList(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...edit };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      if (edit.id) await api.put(`/site-messages/admin/${edit.id}`, payload);
      else await api.post("/site-messages/admin", payload);
      toast.success("Gespeichert");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.put(`/site-messages/admin/${m.id}`, { active: !m.active });
      toast.success(m.active ? "Deaktiviert" : "Live!");
      load();
    } catch {
      toast.error("Fehler");
    }
  };

  const del = async (m) => {
    if (!window.confirm("Meldung wirklich löschen?")) return;
    try {
      await api.delete(`/site-messages/admin/${m.id}`);
      toast.success("Gelöscht");
      load();
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div data-testid="admin-messages">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="text-sm opacity-70">{list.length} Meldungen</div>
        <div className="flex gap-2">
          <button onClick={() => setEdit({ ...DEFAULT_BANNER })} className="btn-primary !py-2 !px-4" data-testid="new-banner">
            <Megaphone className="w-4 h-4" /> Neuer Banner
          </button>
          <button onClick={() => setEdit({ ...DEFAULT_POPUP })} className="btn-secondary !py-2 !px-4" data-testid="new-popup">
            <MessageSquare className="w-4 h-4" /> Neues Popup
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="plant-card p-8 text-center text-sm opacity-70">
          Noch keine Meldungen. Erstelle deinen ersten Banner oder Popup.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((m) => (
            <div key={m.id} className="plant-card p-4 flex items-start gap-3" data-testid={`msg-row-${m.id}`}>
              {/* Color swatch + kind */}
              <div className="flex flex-col items-center gap-1 shrink-0">
                <div
                  className="w-10 h-10 rounded-lg border flex items-center justify-center"
                  style={{ backgroundColor: m.bg_color || "#5a6e3f", color: m.text_color || "#FCF7EC", borderColor: "var(--wp-border)" }}
                  title={`${m.bg_color} / ${m.text_color}`}
                >
                  {m.kind === "popup" ? <MessageSquare className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
                </div>
                <span className="text-[10px] uppercase tracking-wider opacity-60">
                  {m.kind === "popup" ? "Popup" : `Banner ${m.position || "top"}`}
                </span>
              </div>
              {/* Content preview */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {m.title && <span className="font-medium text-sm truncate">{m.title}</span>}
                  <span
                    className="text-[10px] uppercase px-2 py-0.5 rounded-full font-semibold"
                    style={{
                      backgroundColor: m.active ? "rgba(90,110,63,0.18)" : "rgba(0,0,0,0.06)",
                      color: m.active ? "var(--wp-olive)" : "var(--wp-text-muted)",
                    }}
                  >
                    {m.active ? "● Live" : "Entwurf"}
                  </span>
                </div>
                <div className="text-xs opacity-80 line-clamp-2">{m.message}</div>
                {m.cta_text && (
                  <div className="text-[11px] opacity-60 mt-1">CTA: <strong>{m.cta_text}</strong> → {m.cta_link || "(kein Link)"}</div>
                )}
              </div>
              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => toggleActive(m)}
                  className="text-xs px-3 py-1.5 rounded-full font-medium"
                  style={{
                    backgroundColor: m.active ? "var(--wp-cream)" : "var(--wp-olive)",
                    color: m.active ? "var(--wp-olive)" : "var(--wp-cream)",
                    border: m.active ? "1px solid var(--wp-olive)" : "none",
                  }}
                  data-testid={`msg-toggle-${m.id}`}
                >
                  {m.active ? <><EyeOff className="w-3 h-3 inline mr-1" />Pausieren</> : <><Eye className="w-3 h-3 inline mr-1" />Aktivieren</>}
                </button>
                <button onClick={() => setEdit({ ...m })} className="text-sm px-2 py-1 text-[var(--wp-primary)]" data-testid={`msg-edit-${m.id}`}>Bearbeiten</button>
                <button onClick={() => del(m)} className="text-sm px-2 py-1 text-[var(--wp-toxic)]" data-testid={`msg-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={() => setEdit(null)}>
          <form onSubmit={save} className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="msg-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl" style={{ color: "var(--wp-olive)" }}>
                {edit.id ? "Bearbeiten" : "Neu"}: {edit.kind === "popup" ? "Popup" : "Banner"}
              </h3>
              <button type="button" onClick={() => setEdit(null)} className="text-sm opacity-60">Abbrechen</button>
            </div>

            {/* Live Preview */}
            <div className="rounded-xl border mb-5 overflow-hidden" style={{ borderColor: "var(--wp-border)" }}>
              <div className="text-[10px] uppercase tracking-wider opacity-60 px-3 py-1.5 bg-[var(--wp-cream)] border-b" style={{ borderColor: "var(--wp-border)" }}>Vorschau</div>
              <PreviewBox msg={edit} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <label className="col-span-2">
                <div className="overline mb-1">Typ</div>
                <select value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-kind">
                  <option value="banner">Banner (schmal, oben oder unten)</option>
                  <option value="popup">Popup (Modal in der Mitte)</option>
                </select>
              </label>
              {edit.kind === "banner" && (
                <label>
                  <div className="overline mb-1">Position</div>
                  <select value={edit.position || "top"} onChange={(e) => setEdit({ ...edit, position: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-position">
                    <option value="top">Oben (über Header)</option>
                    <option value="bottom">Unten (über Footer)</option>
                  </select>
                </label>
              )}
              {edit.kind === "popup" && (
                <label>
                  <div className="overline mb-1">Verzögerung (Sek.)</div>
                  <input type="number" min="0" max="60" value={edit.delay_seconds ?? 7} onChange={(e) => setEdit({ ...edit, delay_seconds: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-delay" />
                </label>
              )}
              <label className="col-span-2">
                <div className="overline mb-1">Titel (optional, fett angezeigt)</div>
                <input value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-title" />
              </label>
              <label className="col-span-2">
                <div className="overline mb-1">Nachricht *</div>
                <textarea required rows={3} value={edit.message || ""} onChange={(e) => setEdit({ ...edit, message: e.target.value })} className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-message" />
              </label>
              <label>
                <div className="overline mb-1">CTA-Text (optional)</div>
                <input value={edit.cta_text || ""} onChange={(e) => setEdit({ ...edit, cta_text: e.target.value })} placeholder="Jetzt buchen" className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-cta-text" />
              </label>
              <label>
                <div className="overline mb-1">CTA-Link</div>
                <input value={edit.cta_link || ""} onChange={(e) => setEdit({ ...edit, cta_link: e.target.value })} placeholder="/tours" className="w-full px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-cta-link" />
              </label>
              <label>
                <div className="overline mb-1">Hintergrundfarbe</div>
                <input type="color" value={edit.bg_color || "#5a6e3f"} onChange={(e) => setEdit({ ...edit, bg_color: e.target.value })} className="h-10 w-20 rounded border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-bg-color" />
              </label>
              <label>
                <div className="overline mb-1">Textfarbe</div>
                <input type="color" value={edit.text_color || "#FCF7EC"} onChange={(e) => setEdit({ ...edit, text_color: e.target.value })} className="h-10 w-20 rounded border" style={{ borderColor: "var(--wp-border)" }} data-testid="msg-text-color" />
              </label>
              <div className="col-span-2">
                <div className="overline mb-1">Bild / Icon (optional)</div>
                <div className="flex items-center gap-3">
                  <ImageUpload value={edit.image_url} onChange={(v) => setEdit({ ...edit, image_url: v })} endpoint="/admin/uploads" testId="msg-image-upload" />
                  <input placeholder="oder URL" value={edit.image_url || ""} onChange={(e) => setEdit({ ...edit, image_url: e.target.value })} className="flex-1 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--wp-border)" }} />
                </div>
              </div>
              <label className="col-span-2 flex items-center gap-2 cursor-pointer mt-1">
                <input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} data-testid="msg-active" />
                <span className="text-sm font-medium">Direkt aktivieren (live auf der Website sichtbar)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setEdit(null)} className="btn-secondary">Abbrechen</button>
              <button type="submit" className="btn-primary" data-testid="msg-save">
                <Save className="w-4 h-4" /> Speichern
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function PreviewBox({ msg }) {
  const bg = msg.bg_color || "#5a6e3f";
  const fg = msg.text_color || "#FCF7EC";
  if (msg.kind === "popup") {
    return (
      <div className="p-6 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.04)" }}>
        <div className="rounded-3xl p-6 max-w-sm text-center" style={{ backgroundColor: bg, color: fg }}>
          {msg.image_url && <img src={msg.image_url} alt="" className="mx-auto h-14 w-14 rounded-full object-cover mb-3 border-2" style={{ borderColor: fg }} />}
          {msg.title && <h4 className="font-display text-xl mb-1">{msg.title}</h4>}
          {msg.message && <p className="text-xs leading-relaxed mb-3" style={{ opacity: 0.92 }}>{msg.message}</p>}
          {msg.cta_text && <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: fg, color: bg }}>{msg.cta_text} →</span>}
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 py-2.5 flex items-center gap-3" style={{ backgroundColor: bg, color: fg }}>
      {msg.image_url && <img src={msg.image_url} alt="" className="h-6 w-6 rounded object-cover" />}
      <div className="flex-1 text-sm">
        {msg.title && <strong className="mr-2">{msg.title}</strong>}
        {msg.message}
      </div>
      {msg.cta_text && <span className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold" style={{ backgroundColor: fg, color: bg }}>{msg.cta_text} →</span>}
      <span className="opacity-70">×</span>
    </div>
  );
}
